import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { classifyJobActivityStatus } from "@vida/surf-core";
import { SurfActivityPanel } from "./SurfActivityPanel";
import { useSurfCreditsBalance } from "../../hooks/useSurfCreditsBalance";
import { useSurfActivityFeed } from "../../hooks/useSurfActivityFeed";
import { useToast } from "../../ui/useToast";

type Props = {
  title: string;
  saveLabel?: string;
  backTo?: string;
  backLabel?: string;
  extra?: ReactNode;
};

export function SurfTopbar({
  title,
  saveLabel,
  backTo = "/",
  backLabel = "← Libreria",
  extra,
}: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { balanceLabel, loading: creditsLoading, unavailable: creditsUnavailable } =
    useSurfCreditsBalance();
  const { jobs, loading, error, refresh } = useSurfActivityFeed(true);
  const toast = useToast();
  const notifiedRef = useRef<Set<string>>(new Set());
  const feedInitializedRef = useRef(false);

  const runningCount = jobs.filter((j) => classifyJobActivityStatus(j.status) === "running").length;
  const failedRecent = jobs.filter((j) => classifyJobActivityStatus(j.status) === "failed");

  // Toast only jobs that fail *after* the first feed load — not historical failures on refresh.
  useEffect(() => {
    if (!feedInitializedRef.current) {
      for (const job of failedRecent) {
        notifiedRef.current.add(`${job.jobId}-failed`);
      }
      feedInitializedRef.current = true;
      return;
    }
    for (const job of failedRecent) {
      const key = `${job.jobId}-failed`;
      if (notifiedRef.current.has(key)) continue;
      notifiedRef.current.add(key);
      toast.error(job.errorMessage ?? `Job ${job.feature} fallito`);
    }
  }, [failedRecent, toast]);

  return (
    <>
      <header className="surf-topbar">
        <div className="surf-topbar-start">
          <Link to={backTo} className="surf-topbar-back">
            {backLabel}
          </Link>
          <h1 className="surf-topbar-title">{title}</h1>
        </div>
        <div className="surf-topbar-end">
          {saveLabel && <span className="surf-topbar-save muted">{saveLabel}</span>}
          <span
            className="surf-topbar-credits"
            title={
              creditsUnavailable
                ? "Crediti: deploy admin-api con audience Cognito web_surf richiesto"
                : "Crediti disponibili"
            }
          >
            {creditsLoading ? "…" : balanceLabel}
          </span>
          <button
            type="button"
            className="surf-topbar-bell surf-icon-btn"
            aria-label={`Attività AI${runningCount ? `, ${runningCount} in corso` : ""}`}
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((v) => !v)}
          >
            🔔
            {runningCount > 0 && (
              <span className="surf-topbar-badge" aria-hidden>
                {runningCount}
              </span>
            )}
          </button>
          {extra}
        </div>
      </header>

      {drawerOpen && (
        <>
          <button
            type="button"
            className="surf-drawer-backdrop"
            aria-label="Chiudi attività"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="surf-activity-drawer" aria-label="Feed attività">
            <div className="surf-activity-drawer-head">
              <h2>Attività</h2>
              <button
                type="button"
                className="surf-icon-btn"
                aria-label="Chiudi"
                onClick={() => setDrawerOpen(false)}
              >
                ×
              </button>
            </div>
            <SurfActivityPanel jobs={jobs} loading={loading} error={error} onRefresh={refresh} />
          </aside>
        </>
      )}
    </>
  );
}
